/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
// Chakra imports
import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    Spinner,
    useColorModeValue,
    Center,
    useToast,
} from "@chakra-ui/react";
// Custom components
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/gemhsmed.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useLoginMutation } from "../../auth/authApiSlice";
import { setCredentials } from "../../auth/authSlice";
import PageTitle from "components/title";

const Login = () => {
    const userRef = useRef();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [errMsg, setErrMsg] = useState("");
    const [login, { isLoading, error }] = useLoginMutation();
    const navigate = useNavigate(); // Initialize useNavigate
    const dispatch = useDispatch();
    const toast = useToast();

    useEffect(() => {
        userRef.current.focus();
    }, []);

    useEffect(() => {
        setErrMsg("");
    }, [username, password]);

    const handleLogin = async (e) => {
        e.preventDefault();

        try {
            const { access } = await login({ username, password }).unwrap();

            dispatch(setCredentials({ username, token: access }));

            //Show success toast
            toast({
                title: "Login Successful",
                description: `Welcome back, ${username}!`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            setTimeout(() => {
                navigate("/home");
            }, 500);
            // navigate("/home");
        } catch (err) {
            // RTK Query throws an object like { status, data, error, ... }
            const status = err?.status;
            const detail = err?.data?.detail;


            let description;
            if (status === 401) {
                description = "Invalid credentials. Please try again.";
            } else if (status >= 500) {
                description = 
                    "Server error. Please try again later or contact support."; 
            } else if (status === 0 || !status) {
                // network or CORS failure
                description =
                    "No response from the server. Check your network connection.";
            } else {
                // any other 4xx
                description =
                    detail ||
                    "Login failed due to an unexpected error. Please try again.";
            }

            // Show error toast
            toast({
                title: "Login Error",
                description,
                status: "error",
                duration: 5000,
                isClosable: true,
            });

            // Optionally also set your errMsg state if you show inline errors
            setErrMsg(description);
        }
    };

    // Chakra color mode
    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = "gray.400";
    const textColorBrand = useColorModeValue("brand.500", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");
    const [show, setShow] = React.useState(false);
    const handleClick = () => setShow(!show);

    const handleUserInput = (e) => setUsername(e.target.value);

    const handlePwdInput = (e) => setPassword(e.target.value);
    return (
        <>
            <PageTitle title="Login" />
            <DefaultAuth
                illustrationBackground={illustration}
                imagePadding="30px"
                imageSize="70%"
            >
                <Flex
                    maxW={{ base: "100%", md: "max-content" }}
                    w="100%"
                    mx={{ base: "auto", lg: "0px" }}
                    me="auto"
                    h="100%"
                    alignItems="start"
                    justifyContent="center"
                    mb={{ base: "30px", md: "60px" }}
                    px={{ base: "25px", md: "0px" }}
                    mt={{ base: "40px", md: "14vh" }}
                    flexDirection="column"
                >
                    <Box me="auto" mb={3}>
                        <Heading color={textColor} fontSize="36px" mb="10px">
                            Sign In
                        </Heading>
                    </Box>
                    <Flex
                        zIndex="2"
                        direction="column"
                        w={{ base: "100%", md: "420px" }}
                        maxW="100%"
                        background="transparent"
                        borderRadius="15px"
                        mx={{ base: "auto", lg: "unset" }}
                        me="auto"
                        mb={{ base: "20px", md: "auto" }}
                    >
                        <form onSubmit={handleLogin}>
                            <FormControl>
                                <FormLabel
                                    display="flex"
                                    ms="4px"
                                    fontSize="md"
                                    fontWeight="500"
                                    color={textColor}
                                    mb="8px"
                                >
                                    Username<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    isRequired={true}
                                    variant="auth"
                                    fontSize="md"
                                    ref={userRef}
                                    ms={{ base: "0px", md: "0px" }}
                                    type="text"
                                    placeholder="Username"
                                    mb="24px"
                                    fontWeight="500"
                                    size="lg"
                                    value={username}
                                    id="usr"
                                    onChange={handleUserInput}
                                />
                                <FormLabel
                                    ms="4px"
                                    fontSize="md"
                                    fontWeight="500"
                                    color={textColor}
                                    display="flex"
                                >
                                    Password<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <InputGroup size="md">
                                    <Input
                                        isRequired={true}
                                        fontSize="md"
                                        placeholder="Min. 8 characters"
                                        mb="24px"
                                        size="lg"
                                        type={show ? "text" : "password"}
                                        variant="auth"
                                        id="pwd"
                                        value={password}
                                        onChange={handlePwdInput}
                                    />
                                    <InputRightElement
                                        display="flex"
                                        alignItems="center"
                                        mt="4px"
                                    >
                                        <Icon
                                            color={textColorSecondary}
                                            _hover={{ cursor: "pointer" }}
                                            as={
                                                show
                                                    ? RiEyeCloseLine
                                                    : MdOutlineRemoveRedEye
                                            }
                                            onClick={handleClick}
                                        />
                                    </InputRightElement>
                                </InputGroup>
                                <Flex
                                    justifyContent="space-between"
                                    align="center"
                                    mb="24px"
                                >
                                    <FormControl
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <Checkbox
                                            id="remember-login"
                                            colorScheme="brandScheme"
                                            me="10px"
                                        />
                                        <FormLabel
                                            htmlFor="remember-login"
                                            mb="0"
                                            fontWeight="normal"
                                            color={textColor}
                                            fontSize="sm"
                                        >
                                            Keep me logged in
                                        </FormLabel>
                                    </FormControl>
                                    <NavLink to="/auth/forgot-password">
                                        <Text
                                            color={textColorBrand}
                                            fontSize="sm"
                                            w="124px"
                                            fontWeight="500"
                                        >
                                            Forgot password?
                                        </Text>
                                    </NavLink>
                                </Flex>
                                <Center>
                                    <Button
                                        fontSize="sm"
                                        variant="brand"
                                        fontWeight="500"
                                        type="submit"
                                        w="50%"
                                        h="50"
                                        mb="24px"
                                        isLoading={isLoading}
                                        loadingText="Loading..."
                                        spinner={<Spinner />}
                                    >
                                        Login
                                    </Button>
                                </Center>
                            </FormControl>
                        </form>
                        {error && (
                            <Text color="red.500" mt={2}>
                                {error.message}
                            </Text>
                        )}
                    </Flex>
                </Flex>
            </DefaultAuth>
        </>
    );
};

export default Login;
